"use client";

import * as React from "react";
import { Filter } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useLocalization } from "@/context/localization-context";

interface TableFiltersDialogProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  filterCount?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function TableFiltersDialog({
  children,
  title,
  description,
  filterCount = 0,
  open,
  onOpenChange,
}: TableFiltersDialogProps) {
  const { t } = useLocalization();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9">
          <Filter className="h-4 w-4" />
          {t("common.filters")}
          {filterCount > 0 && (
            <span className="ml-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[10px] leading-none text-primary-foreground">
              {filterCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      {/* header and footer stay put, the filters scroll between them (phones show a few at a time) */}
      <DialogContent className="flex max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b p-4 pr-12 sm:px-6">
          <DialogTitle>{title ?? t("list.filtersDialog.title")}</DialogTitle>
          <DialogDescription>{description ?? t("list.filtersDialog.description")}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:px-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
        </div>
        <DialogFooter className="border-t p-3 sm:px-6">
          <Button className="w-full sm:w-auto" onClick={() => onOpenChange?.(false)}>
            {t("common.done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
